import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Field, Input, Select } from '@/ui/Field';
import { SkeletonFilas } from '@/ui/Skeleton';
import { Avatar } from '@/components/Avatar';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { Icono } from '@/components/Icono';
import {
  useAgregarHijo, useCatalogos, useExpediente, useHijos, useQuitarHijo, useSembrarExpediente,
  type Campo, type Hijo, type Registro,
} from './api';
import { Correo } from './Correo';
import { FormularioDeRegistro } from './FormularioDeRegistro';
import { Versiones } from './Versiones';
import { CabeceraDeZona, ValorDeCampo, fecha } from './piezas';

/**
 * Expediente — la ficha de un colaborador.
 * `#recursos-privados/expediente/<id>`.
 *
 * ## Lo que cambia
 *
 * - **Tiene dirección.** Era una vista que solo se alcanzaba abriendo un
 *   diálogo y eligiendo a la persona; recargar sacaba de ella.
 * - **Si los hijos fallan, se dice.** El vanilla los pedía con
 *   `.catch(() => [])`: una consulta caída y una persona sin hijos se veían
 *   exactamente igual, y la ficha deriva de aquí «¿Tiene hijos?».
 * - **La fecha de nacimiento no se corre un día.** `Utils.fmtDate` sobre una
 *   columna DATE restaba la zona horaria.
 */
export function Expediente({ userId }: { userId: number }) {
  const consulta = useExpediente(userId);
  const sembrar = useSembrarExpediente();

  const [nuevoEn, setNuevoEn] = useState<number | null>(null);
  const [editando, setEditando] = useState<{ dominio: number; entrada: number } | null>(null);
  const [versionesDe, setVersionesDe] = useState<number | null>(null);
  const [correoDe, setCorreoDe] = useState<number | null>(null);

  const d = consulta.data;
  const dominios = d?.domains ?? [];
  const abierto = dominios.find((x) => x.id === (nuevoEn ?? editando?.dominio));

  const r = d?.resolved ?? {};
  const datos: Array<[string, string | null | undefined]> = [
    ['Empresa', r.company], ['Sede', r.sede], ['Cargo', r.position_resolved], ['Área', r.area],
  ];

  return (
    <>
      <CabeceraDeZona
        icono="bi-folder2-open"
        titulo={<>Expediente {d && <span className="truncate font-normal text-ink-2">· {d.user.full_name}</span>}</>}
      >
        {d && (
          <span className="text-2 text-muted">
            {d.total_records} {d.total_records === 1 ? 'registro' : 'registros'} en total
          </span>
        )}
      </CabeceraDeZona>

      {consulta.isPending ? (
        <SkeletonFilas filas={5} />
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="el expediente"
          onReintentar={() => void consulta.refetch()}
          onVolver={() => { location.hash = '#recursos-privados'; }}
        />
      ) : !d ? null : (
        <>
          <Card className="mb-4 p-4">
            <div className="flex flex-wrap items-center gap-4">
              <Avatar nombre={d.user.full_name} tamano={52} />
              <div className="min-w-0 flex-1">
                <h2 className="text-5 font-bold text-brand-ink">{d.user.full_name}</h2>
                <p className="text-2 text-muted">CC {d.user.cedula} · {d.user.email}</p>
                <dl className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-2">
                  {datos.filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex gap-1">
                      <dt className="font-semibold text-brand-ink">{k}:</dt>
                      <dd className="text-ink-2">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
            {!!d.tags?.length && (
              <ul className="mt-3 flex flex-wrap gap-1.5">
                {d.tags.map((t) => <li key={t}><Badge tone="info">{t}</Badge></li>)}
              </ul>
            )}
          </Card>

          <Hijos userId={userId} />

          {dominios.length ? dominios.map((dom) => (
            <SubDominio
              key={dom.id}
              nombre={dom.name}
              icono={dom.icon}
              campos={dom.fields ?? []}
              entradas={dom.entries}
              onAgregar={() => setNuevoEn(dom.id)}
              onEditar={(eid) => setEditando({ dominio: dom.id, entrada: eid })}
              onVersiones={setVersionesDe}
              onCorreo={setCorreoDe}
            />
          )) : (
            <EstadoVacio
              icono="bi-diagram-3"
              titulo="Sin sub-dominios configurados"
              descripcion="Incapacidades, vacaciones, dotación… se crean de una vez para todo el portal."
              accion={{
                texto: 'Configurar sub-dominios',
                onClick: () => sembrar.mutate(undefined, {
                  onSuccess: (s) => Utils.toast(
                    s.created.length
                      ? `${s.created.length} sub-dominio${s.created.length === 1 ? ' creado' : 's creados'}`
                      : 'Los sub-dominios ya estaban configurados',
                    'success'),
                  onError: (err) => Utils.toast(
                    err instanceof Error ? err.message : 'No se pudo configurar', 'error'),
                }),
              }}
            />
          )}
        </>
      )}

      {abierto && (
        <FormularioDeRegistro
          id={editando?.entrada ?? null}
          recursoId={abierto.id}
          campos={abierto.fields ?? []}
          esSistema={!!abierto.is_system}
          pideColaborador
          colaboradorFijo={userId}
          onCerrar={() => { setNuevoEn(null); setEditando(null); }}
        />
      )}
      {versionesDe != null && <Versiones id={versionesDe} onCerrar={() => setVersionesDe(null)} />}
      {correoDe != null && <Correo id={correoDe} onCerrar={() => setCorreoDe(null)} />}
    </>
  );
}

/* ── Un sub-dominio ────────────────────────────────────────────────────── */

function SubDominio({
  nombre, icono, campos, entradas, onAgregar, onEditar, onVersiones, onCorreo,
}: {
  nombre: string;
  icono: string;
  campos: Campo[];
  entradas: Registro[];
  onAgregar: () => void;
  onEditar: (id: number) => void;
  onVersiones: (id: number) => void;
  onCorreo: (id: number) => void;
}) {
  // Cinco columnas: dentro del expediente la tabla es un resumen; la vista
  // completa del sub-dominio es su propia pantalla.
  const cols = campos.slice(0, 5);

  return (
    <Card className="mb-3">
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
        <span className="text-5 text-brand-ink" aria-hidden="true">
          <Icono nombre={icono} respaldo="bi-database" />
        </span>
        <h3 className="text-3 font-bold text-brand-ink">{nombre}</h3>
        <span className="text-1 text-muted">
          {entradas.length} {entradas.length === 1 ? 'registro' : 'registros'}
        </span>
        <Button size="sm" className="ml-auto" onClick={onAgregar}>
          <i className="bi bi-plus-lg" aria-hidden="true" /> Agregar
        </Button>
      </header>

      {entradas.length ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-2">
            <thead>
              <tr className="border-b border-line bg-sunken text-left">
                {cols.map((f) => (
                  <th key={f.key} scope="col" className="whitespace-nowrap px-3 py-2 font-semibold text-brand-ink">
                    {f.label}
                  </th>
                ))}
                <th scope="col" className="px-3 py-2"><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody>
              {entradas.map((e) => {
                const cual = String((e.data ?? {})[cols[0]?.key ?? ''] ?? `registro ${e.id}`);
                return (
                  <tr key={e.id} className="border-b border-line last:border-0 hover:bg-sunken">
                    {cols.map((f, i) => (
                      <td key={f.key} className="px-3 py-2 align-top">
                        {i === 0 ? (
                          <button
                            type="button"
                            className="text-left font-semibold text-brand-ink hover:underline focus-visible:focus-anillo"
                            onClick={() => onEditar(e.id)}
                          >
                            <ValorDeCampo campo={f} valor={(e.data ?? {})[f.key]} />
                          </button>
                        ) : (
                          <ValorDeCampo campo={f} valor={(e.data ?? {})[f.key]} />
                        )}
                      </td>
                    ))}
                    <td className="whitespace-nowrap px-2 py-1 text-right align-top">
                      <span className="mr-1 text-1 text-muted">v{e.version}</span>
                      <Button
                        variant="ghost" size="sm" iconOnly
                        aria-label={`Historial de ${cual}`}
                        onClick={() => onVersiones(e.id)}
                      >
                        <i className="bi bi-clock-history" aria-hidden="true" />
                      </Button>
                      <Button
                        variant="ghost" size="sm" iconOnly
                        aria-label={`Enviar ${cual} por correo`}
                        onClick={() => onCorreo(e.id)}
                      >
                        <i className="bi bi-envelope" aria-hidden="true" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="px-4 py-3 text-2 text-muted">Sin registros en este sub-dominio.</p>
      )}
    </Card>
  );
}

/* ── Hijos ─────────────────────────────────────────────────────────────── */

/**
 * Los hijos del colaborador.
 *
 * Viven aquí y no en Mi perfil porque los tres endpoints son de staff: es un
 * dato que carga Bienestar. Y van fuera de los sub-dominios porque no son un
 * recurso configurable —tienen tabla propia, `collaborator_children`— y su
 * columna en el maestro es calculada.
 */
function Hijos({ userId }: { userId: number }) {
  const consulta = useHijos(userId);
  const quitar = useQuitarHijo();
  const [agregando, setAgregando] = useState(false);
  const [quitando, setQuitando] = useState<Hijo | null>(null);

  const hijos = consulta.data ?? [];

  return (
    <Card className="mb-3">
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
        <i className="bi bi-people-fill text-5 text-brand-ink" aria-hidden="true" />
        <h3 className="text-3 font-bold text-brand-ink">Hijos</h3>
        {!consulta.isError && (
          <span className="text-1 text-muted">
            {hijos.length} {hijos.length === 1 ? 'registrado' : 'registrados'}
          </span>
        )}
        <Button size="sm" className="ml-auto" onClick={() => setAgregando(true)}>
          <i className="bi bi-plus-lg" aria-hidden="true" /> Agregar
        </Button>
      </header>

      {consulta.isPending ? (
        <div className="p-4"><SkeletonFilas filas={2} columnas={3} /></div>
      ) : consulta.isError ? (
        // Un fallo aquí no puede pasar por «no tiene hijos»: de esta lista sale
        // la columna «¿Tiene hijos?» del maestro.
        <div className="p-4">
          <EstadoError
            err={consulta.error}
            que="los hijos registrados"
            onReintentar={() => void consulta.refetch()}
          />
        </div>
      ) : !hijos.length ? (
        <p className="px-4 py-3 text-2 text-muted">
          Sin hijos registrados. Mientras esto esté vacío, la ficha dice «No» en «¿Tiene hijos?»:
          esa columna se calcula desde aquí.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-2">
            <thead>
              <tr className="border-b border-line bg-sunken text-left">
                {['Nombre', 'Fecha de nacimiento', 'Edad', 'Género'].map((h) => (
                  <th key={h} scope="col" className="whitespace-nowrap px-3 py-2 font-semibold text-brand-ink">
                    {h}
                  </th>
                ))}
                <th scope="col" className="px-3 py-2"><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody>
              {hijos.map((h) => (
                <tr key={h.id} className="border-b border-line last:border-0">
                  <td className="px-3 py-2 text-ink">{h.full_name}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-ink-2">{fecha(h.birth_date)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-ink-2">
                    {h.age == null ? '—' : `${h.age} ${h.age === 1 ? 'año' : 'años'}`}
                  </td>
                  <td className="px-3 py-2 text-ink-2">{h.gender || '—'}</td>
                  <td className="px-2 py-1 text-right">
                    <Button
                      variant="ghost" size="sm" iconOnly
                      aria-label={`Quitar a ${h.full_name}`}
                      onClick={() => setQuitando(h)}
                    >
                      <i className="bi bi-x-lg text-danger-ink" aria-hidden="true" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {agregando && <FormularioDeHijo userId={userId} onCerrar={() => setAgregando(false)} />}

      <ConfirmDialog
        abierto={quitando != null}
        onCambio={(v) => { if (!v) setQuitando(null); }}
        titulo="Quitar el registro"
        descripcion={`Se elimina a ${quitando?.full_name ?? ''} y el conteo de la ficha baja. Queda constancia en la auditoría.`}
        textoConfirmar="Quitar"
        destructivo
        cargando={quitar.isPending}
        onConfirmar={() => {
          if (!quitando) return;
          quitar.mutate(quitando.id, {
            onSuccess: () => { Utils.toast('Registro eliminado', 'success'); setQuitando(null); },
            onError: (err) => {
              setQuitando(null);
              Utils.toast(err instanceof Error ? err.message : 'No se pudo quitar', 'error');
            },
          });
        }}
      />
    </Card>
  );
}

function FormularioDeHijo({ userId, onCerrar }: { userId: number; onCerrar: () => void }) {
  const agregar = useAgregarHijo();
  // El género sale del catálogo del sistema, el mismo que usa el maestro: así
  // «F», «fem» y «Femenino» no vuelven a convivir como tres valores distintos.
  const catalogos = useCatalogos(true, true);
  const generos = catalogos.data?.find((f) => f.key === 'gender')?.options ?? [];

  const [nombre, setNombre] = useState('');
  const [nacimiento, setNacimiento] = useState('');
  const [genero, setGenero] = useState('');
  const [error, setError] = useState('');

  return (
    <Dialog
      abierto
      onCambio={(v) => { if (!v && !agregar.isPending) onCerrar(); }}
      titulo="Agregar hijo"
      descripcion="La fecha de nacimiento no es obligatoria, pero sin ella no hay edad que calcular ni rango por el que segmentar, que es para lo que sirve este dato."
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button
            loading={agregar.isPending}
            onClick={() => {
              if (!nombre.trim()) { setError('Escribe el nombre completo.'); return; }
              agregar.mutate(
                {
                  userId,
                  cuerpo: {
                    full_name: nombre.trim(),
                    ...(nacimiento ? { birth_date: nacimiento } : {}),
                    ...(genero ? { gender: genero } : {}),
                  },
                },
                {
                  onSuccess: () => { Utils.toast('Hijo registrado', 'success'); onCerrar(); },
                  onError: (err) => setError(
                    err instanceof Error ? err.message : 'No se pudo guardar'),
                },
              );
            }}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 pb-2">
        <Field label="Nombre completo" error={error || undefined}>
          <Input
            value={nombre}
            onChange={(e) => { setNombre(e.target.value); setError(''); }}
            placeholder="Nombre y apellidos"
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Fecha de nacimiento" opcional>
            <Input type="date" value={nacimiento} onChange={(e) => setNacimiento(e.target.value)} />
          </Field>
          <Field
            label="Género"
            opcional
            ayuda={generos.length ? undefined : 'No hay catálogo de género configurado.'}
          >
            <Select
              value={genero}
              disabled={!generos.length}
              onChange={(e) => setGenero(e.target.value)}
            >
              <option value="">— Sin especificar —</option>
              {generos.map((o) => <option key={o} value={o}>{o}</option>)}
            </Select>
          </Field>
        </div>
      </div>
    </Dialog>
  );
}
